// =====================================================
// components/SubmissionCard.tsx
// =====================================================
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs et structure de données restent codés en dur, inchangés.

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, Text, View, TouchableOpacity,
  FlatList, Platform, Dimensions,
} from 'react-native';
import { Ionicons }           from '@expo/vector-icons';
import { LinearGradient }     from 'expo-linear-gradient';
import * as Haptics           from 'expo-haptics';
import { useTranslation }     from 'react-i18next';
import { SafeImage, VideoThumbPreview, ZoomImageViewer } from './MediaViewers';
import {
  Submission, VideoFeedItem, AudioFeedItem, GameType,
  NATIVE, getInitials,
} from '../lib/feedHelpers';

const { width } = Dimensions.get('window');

interface SubmissionCardProps {
  sub:            Submission;
  allVideoItems:  VideoFeedItem[];
  allAudioItems:  AudioFeedItem[];
  onVote:         (gameType: GameType, runId: string, voteForUserId: string, sessionId: string, currentlyVoted: boolean) => Promise<void>;
  openVideoModal: (items: VideoFeedItem[], startIndex: number) => void;
  openAudioModal: (items: AudioFeedItem[], startIndex: number) => void;
  onAuthorPress:  (authorId: string) => void;
}

const SubmissionCard = React.memo(({
  sub, onVote, allVideoItems, allAudioItems, openVideoModal, openAudioModal, onAuthorPress,
}: SubmissionCardProps) => {
  const { t } = useTranslation();
  const [totalVotes,  setTotalVotes]  = useState(sub.total_votes);
  const [userVoted,   setUserVoted]   = useState(sub.user_voted);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVoting,    setIsVoting]    = useState(false);
  const [zoomUrl,     setZoomUrl]     = useState<string | null>(null);
  const lastTapRef = useRef<{ time: number; id: string } | null>(null);

  const handleImageDoubleTap = (itemId: string, url: string) => {
    const now = Date.now();
    if (lastTapRef.current && lastTapRef.current.id === itemId && now - lastTapRef.current.time < 350) {
      if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      setZoomUrl(url);
      lastTapRef.current = null;
    } else {
      lastTapRef.current = { time: now, id: itemId };
    }
  };

  useEffect(() => { setTotalVotes(sub.total_votes); setUserVoted(sub.user_voted); }, [sub]);

  const isPerformance = sub.game_type === 'performance';
  const isArtisanat   = sub.game_type === 'artisanat';
  const isVideo       = isPerformance || isArtisanat;
  const isMusic       = sub.game_type === 'music';

  const media = sub.media ?? ((sub as any).images ?? []).map((img: any) => ({
    id: img.id, url: img.image_url, description: img.description ?? null,
  }));

  const slideWidth = media.length > 1 ? width - 48 : width - 24;

  const handleVoteLocal = async () => {
    if (isVoting) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setIsVoting(true);
    const n = !userVoted; setUserVoted(n); setTotalVotes(p => n ? p + 1 : p - 1);
    try { await onVote(sub.game_type ?? 'arts', sub.run_id, sub.user_id, sub.session_id, userVoted); }
    catch { setUserVoted(!n); setTotalVotes(p => n ? p - 1 : p + 1); }
    finally { setIsVoting(false); }
  };

  const badgeBg    = isPerformance ? '#FFF3E0' : isArtisanat ? '#FFF8E1' : isMusic ? '#E8F5E9' : '#F0E6FF';
  const badgeColor = isPerformance ? '#E65100' : isArtisanat ? '#B45309' : isMusic ? '#2E7D32' : '#8E44AD';
  const badgeLabel = sub.badge ?? (isArtisanat ? t('submissionCard.badges.artisanat') : t('submissionCard.badges.arts'));

  return (
    <View style={styles.subCard}>
      <ZoomImageViewer visible={!!zoomUrl} url={zoomUrl} onClose={() => setZoomUrl(null)} />

      <View style={styles.subHeader}>
        <View style={[styles.artsBadge, { backgroundColor: badgeBg }]}>
          <Text style={[styles.artsBadgeText, { color: badgeColor }]}>{badgeLabel}</Text>
        </View>
        <TouchableOpacity style={styles.postAuthor} onPress={() => onAuthorPress(sub.user_id)} activeOpacity={0.7}>
          <SafeImage
            uri={sub.author.avatar_url}
            style={styles.avatar}
            resizeMode="cover"
            fallback={
              <View style={[styles.avatarPlaceholder, { backgroundColor: badgeColor }]}>
                <Text style={styles.avatarText}>{getInitials(sub.author.nom, sub.author.prenom)}</Text>
              </View>
            }
          />
          <View>
            <Text style={styles.authorName}>{sub.author.prenom} {sub.author.nom}</Text>
            <Text style={styles.postTime}>{t('submissionCard.runLabel', { number: sub.run_number, title: sub.run_title })}</Text>
          </View>
        </TouchableOpacity>
        <View style={styles.totalVotesBadge}>
          <Ionicons name="heart" size={13} color="#E74C3C" />
          <Text style={styles.totalVotesText}>{totalVotes}</Text>
        </View>
      </View>

      <FlatList
        data={media}
        keyExtractor={item => item.id}
        horizontal
        pagingEnabled={media.length > 1}
        showsHorizontalScrollIndicator={false}
        style={styles.imagesList}
        onMomentumScrollEnd={e => {
          const idx = Math.round(e.nativeEvent.contentOffset.x / slideWidth);
          setActiveIndex(idx);
        }}
        renderItem={({ item, index }) => (
          <View style={[styles.imageSlide, { width: slideWidth }]}>
            {isMusic ? (
              <TouchableOpacity
                activeOpacity={0.9}
                style={styles.audioThumb}
                onPress={() => {
                  const si = allAudioItems.findIndex(a => a.mediaId === item.id);
                  openAudioModal(allAudioItems, si >= 0 ? si : 0);
                }}
              >
                <LinearGradient colors={['#1E0A3C', '#0D0B1A']} style={StyleSheet.absoluteFill} />
                <View style={styles.audioWave}>
                  <Ionicons name="musical-notes" size={52} color="rgba(168,85,247,0.75)" />
                </View>
                <View style={styles.audioPlayIcon}>
                  <Ionicons name="play" size={18} color="rgba(255,255,255,0.9)" />
                </View>
                <View style={styles.audioBadge}>
                  <Ionicons name="musical-notes" size={12} color="#FFF" />
                  <Text style={styles.audioBadgeText}>{t('submissionCard.mediaBadges.audio')}</Text>
                </View>
              </TouchableOpacity>
            ) : isVideo ? (
              <TouchableOpacity
                activeOpacity={0.9}
                style={styles.videoThumbSub}
                onPress={() => {
                  const si = allVideoItems.findIndex(v => v.mediaId === item.id);
                  openVideoModal(allVideoItems, si >= 0 ? si : 0);
                }}
              >
                <VideoThumbPreview uri={item.url} isActive={activeIndex === index} style={StyleSheet.absoluteFill} />
                <View style={styles.videoPlayOverlay}>
                  <Ionicons name="expand-outline" size={22} color="rgba(255,255,255,0.85)" />
                </View>
                <View style={styles.videoBadge}>
                  <Ionicons name="videocam" size={12} color="#FFF" />
                  <Text style={styles.videoBadgeText}>{isArtisanat ? t('submissionCard.mediaBadges.artisanat') : t('submissionCard.mediaBadges.video')}</Text>
                </View>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                activeOpacity={0.95}
                onPress={() => handleImageDoubleTap(item.id, item.url)}
                style={styles.subImageWrapper}
              >
                <SafeImage
                  uri={item.url}
                  style={styles.subImage}
                  resizeMode="contain"
                  fallback={
                    <View style={[styles.subImage, { backgroundColor: '#F0F0F0', justifyContent: 'center', alignItems: 'center' }]}>
                      <Ionicons name="image-outline" size={36} color="#CCC" />
                    </View>
                  }
                />
                <View style={styles.doubleTapHintSub}>
                  <Ionicons name="expand-outline" size={14} color="rgba(255,255,255,0.85)" />
                </View>
              </TouchableOpacity>
            )}
            {media.length > 1 && (
              <View style={styles.imageCounter}>
                <Text style={styles.imageCounterText}>{index + 1}/{media.length}</Text>
              </View>
            )}
            {item.description ? (
              <Text style={styles.imgDesc} numberOfLines={2}>{item.description}</Text>
            ) : null}
          </View>
        )}
      />

      {media.length > 1 && (
        <View style={styles.dots}>
          {media.map((_: any, i: number) => (
            <View key={i} style={[styles.dot, i === activeIndex && styles.dotActive]} />
          ))}
        </View>
      )}

      <TouchableOpacity
        style={[styles.voteBtn, userVoted && styles.voteBtnVoted]}
        onPress={handleVoteLocal}
        disabled={isVoting}
        activeOpacity={0.8}
      >
        {userVoted ? (
          <><Text style={styles.voteBtnText}>{t('submissionCard.voted')}</Text><Text style={styles.voteBtnCount}>{totalVotes}</Text></>
        ) : (
          <><Ionicons name="heart-outline" size={16} color="#FFF" /><Text style={styles.voteBtnText}>{t('submissionCard.vote')}</Text><Text style={styles.voteBtnCount}>{totalVotes}</Text></>
        )}
      </TouchableOpacity>
    </View>
  );
});

export default SubmissionCard;

const styles = StyleSheet.create({
  subCard:          { backgroundColor: '#fff', marginHorizontal: 0, marginVertical: 4, overflow: 'hidden' },
  subHeader:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, gap: 10 },
  artsBadge:        { backgroundColor: '#F0E6FF', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  artsBadgeText:    { color: '#8E44AD', fontSize: 11, fontWeight: '700' },
  postAuthor:       { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  avatar:           { width: 40, height: 40, borderRadius: 20 },
  avatarPlaceholder:{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarText:       { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  authorName:       { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  postTime:         { fontSize: 12, color: '#65676B', marginTop: 1 },
  totalVotesBadge:  { flexDirection: 'row', alignItems: 'center', gap: 4, marginLeft: 'auto' },
  totalVotesText:   { color: '#E74C3C', fontSize: 13, fontWeight: '700' },
  imagesList:       { flexGrow: 0 },
  imageSlide:       { paddingHorizontal: 12 },
  subImageWrapper:  { position: 'relative' },
  subImage:         { width: '100%', height: 280, borderRadius: 12, backgroundColor: '#F8F8F8' },
  doubleTapHintSub: { position: 'absolute', bottom: 10, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 5 },
  videoThumbSub:    { width: '100%', height: 280, borderRadius: 12, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' },
  videoPlayOverlay: { position: 'absolute', top: 10, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 16, padding: 6 },
  videoBadge:       { position: 'absolute', bottom: 12, left: 12, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4 },
  videoBadgeText:   { color: '#FFF', fontSize: 11, fontWeight: '700' },
  audioThumb:       { width: '100%', height: 280, borderRadius: 12, overflow: 'hidden', justifyContent: 'center', alignItems: 'center' },
  audioWave:        { alignItems: 'center', justifyContent: 'center' },
  audioPlayIcon:    { position: 'absolute', top: 10, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 16, padding: 6 },
  audioBadge:       { position: 'absolute', bottom: 12, left: 12, flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4 },
  audioBadgeText:   { color: '#FFF', fontSize: 11, fontWeight: '700' },
  imageCounter:     { position: 'absolute', top: 12, right: 20, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 },
  imageCounterText: { color: '#FFF', fontSize: 11, fontWeight: '700' },
  imgDesc:          { fontSize: 13, color: '#555', marginTop: 8, paddingHorizontal: 4 },
  voteBtn:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, backgroundColor: '#8A2BE2', borderRadius: 25, paddingVertical: 10, paddingHorizontal: 20, marginTop: 10, marginBottom: 14, marginHorizontal: 14, alignSelf: 'center' },
  voteBtnVoted:     { backgroundColor: '#E74C3C' },
  voteBtnText:      { color: '#FFF', fontWeight: '700', fontSize: 14 },
  voteBtnCount:     { color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: '600' },
  dots:             { flexDirection: 'row', justifyContent: 'center', gap: 5, paddingVertical: 10 },
  dot:              { width: 6, height: 6, borderRadius: 3, backgroundColor: '#DDD' },
  dotActive:        { backgroundColor: '#8A2BE2', width: 18, borderRadius: 3 },
});
